import { useOrgStore } from '@/stores/org-store';
import { getFreshToken } from '@/utils/auth-token-provider';
import * as Sentry from '@sentry/react';
type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface ApiRequestProps {
  url: string;
  method?: HttpMethod;
  body?: Record<string, unknown> | FormData;
  formData?: boolean;
  target?: 'platform' | 'workflow' | 'notifications' | 'filestore';
  signal?: AbortSignal;
  /** HTTP status codes that should not be reported to Sentry (e.g., expected 404s) */
  suppressSentryStatuses?: number[];
  /** Extra headers merged on top of Content-Type and Authorization */
  headers?: Record<string, string>;
  /**
   * `text` returns the body as a string (CSV downloads); `blob` returns the
   * raw bytes along with the response headers.
   */
  responseType?: 'json' | 'text' | 'blob';
}

interface ApiResponse<T = unknown> {
  data?: T;
  detail?: string;
  message?: string;
  success?: boolean;
  statusCode?: number;
  /** Set for `responseType: 'blob'`. */
  headers?: Headers;
}

const buildUrl = (url: string, target: ApiRequestProps['target']): string =>
  `/proxy/${target}${url}`;

export const apiRequest = async <T = unknown>({
  url,
  method = 'GET',
  body,
  formData = false,
  target = 'platform',
  signal,
  suppressSentryStatuses = [],
  headers: extraHeaders,
  responseType = 'json',
}: ApiRequestProps): Promise<ApiResponse<T>> => {
  // Declare requestUrl outside try block so it's accessible in catch
  let requestUrl = '';

  try {
    const { authType } = useOrgStore.getState();
    const token = await getFreshToken();

    requestUrl = buildUrl(url, target);

    const headers: Record<string, string> = {};

    if (!formData) {
      headers['Content-Type'] = 'application/json';
    }

    if (
      token &&
      (target === 'platform' ||
        target === 'workflow' ||
        target === 'notifications')
    ) {
      if (authType === 'auth0') {
        headers['Authorization'] = `Bearer ${token}`;
      } else {
        headers['X-User-Token'] = token;
      }
    }

    if (extraHeaders) {
      Object.assign(headers, extraHeaders);
    }

    const payloadBody = formData
      ? (body as FormData)
      : body
        ? JSON.stringify(body)
        : undefined;

    let response = await fetch(requestUrl, {
      method,
      headers,
      body: payloadBody,
      signal,
    });

    // Auth0 401 retry: force-refresh token bypassing SDK cache, retry once.
    // Uses cacheMode: 'off' per Auth0 best practice for 401 recovery.
    if (response.status === 401 && authType === 'auth0') {
      const retryToken = await getFreshToken({ cacheMode: 'off' });
      if (retryToken && retryToken !== token) {
        headers['Authorization'] = `Bearer ${retryToken}`;
        response = await fetch(requestUrl, {
          method,
          headers,
          body: payloadBody,
          signal,
        });
      }
    }

    if (response.status === 409) {
      try {
        const data = await response.json();
        return { detail: data.detail, statusCode: response.status };
      } catch {
        return {
          detail: 'Response parsing error',
          statusCode: response.status,
        };
      }
    }

    if (!response.ok) {
      try {
        const contentType = response.headers.get('content-type');

        // Check if response is JSON
        if (contentType && contentType.includes('application/json')) {
          const data = await response.json();

          // Simple, user-friendly error message
          const errorDetail =
            (typeof data.detail === 'string' ? data.detail : null) ||
            (typeof data.error === 'string' ? data.error : null) ||
            (typeof data.message === 'string' ? data.message : null) ||
            'An unexpected error occurred';

          // Properly serialize error details for Sentry
          let sentryErrorDetail = errorDetail;
          const rawErrorDetail = data.detail || data.error || data.message;

          if (Array.isArray(rawErrorDetail)) {
            // Convert array of errors to readable string for Sentry
            sentryErrorDetail =
              rawErrorDetail
                .map((item) => {
                  if (typeof item === 'string') return item;
                  if (typeof item === 'object' && item !== null) {
                    return (
                      item.msg ||
                      item.message ||
                      item.error ||
                      JSON.stringify(item)
                    );
                  }
                  return String(item);
                })
                .join('; ') || errorDetail;
          } else if (
            typeof rawErrorDetail === 'object' &&
            rawErrorDetail !== null
          ) {
            // Convert error object to readable string for Sentry
            sentryErrorDetail =
              rawErrorDetail.msg ||
              rawErrorDetail.message ||
              rawErrorDetail.error ||
              JSON.stringify(rawErrorDetail);
          }

          // FastAPI/Pydantic validation errors (422) arrive as an array of
          // error objects, which the string check above discards. Show the same
          // joined string we send to Sentry instead of the generic fallback.
          const userErrorDetail = Array.isArray(rawErrorDetail)
            ? sentryErrorDetail
            : errorDetail;

          // Capture HTTP errors in Sentry for monitoring (skip 401/403 auth errors and suppressed statuses)
          if (
            response.status !== 401 &&
            response.status !== 403 &&
            !suppressSentryStatuses.includes(response.status)
          ) {
            Sentry.captureException(
              new Error(`API Error ${response.status}: ${url}`),
              {
                tags: {
                  api_error: true,
                  status_code: response.status,
                  target_api: target,
                },
                extra: {
                  url: requestUrl,
                  method,
                  error_detail_string: sentryErrorDetail,
                  full_response_json: JSON.stringify(data, null, 2),
                },
              },
            );
          }

          return {
            detail: userErrorDetail,
            success: false,
            statusCode: response.status,
            data,
          };
        } else {
          // Handle non-JSON responses (HTML error pages, empty responses, etc.)
          const responseText = await response.text();
          let errorDetail = 'An unexpected error occurred';

          // Try to extract meaningful error information
          if (responseText) {
            if (
              responseText.includes('Internal Server Error') ||
              responseText.includes('500')
            ) {
              errorDetail =
                'Internal server error - the server encountered an unexpected condition';
            } else if (
              responseText.includes('Bad Gateway') ||
              responseText.includes('502')
            ) {
              errorDetail =
                'Bad gateway - the server received an invalid response from upstream';
            } else if (
              responseText.includes('Service Unavailable') ||
              responseText.includes('503')
            ) {
              errorDetail =
                'Service unavailable - the server is temporarily unable to handle the request';
            } else if (
              responseText.includes('Gateway Timeout') ||
              responseText.includes('504')
            ) {
              errorDetail =
                'Gateway timeout - the server did not receive a timely response';
            } else if (
              responseText.includes(
                'Web server is returning an unknown error',
              ) ||
              responseText.includes('520')
            ) {
              errorDetail =
                'Web server error - the server returned an unknown error (likely a crash or misconfiguration)';
            } else {
              errorDetail = `Server error (${response.status}): ${responseText.substring(0, 200)}...`;
            }
          } else {
            errorDetail = `Server error (${response.status}): Empty response from server`;
          }

          // Capture non-JSON response errors in Sentry (skip 401/403 auth errors and suppressed statuses)
          if (
            response.status !== 401 &&
            response.status !== 403 &&
            !suppressSentryStatuses.includes(response.status)
          ) {
            Sentry.captureException(
              new Error(
                `API Non-JSON Error ${response.status}: ${errorDetail}`,
              ),
              {
                tags: {
                  api_non_json_error: true,
                  status_code: response.status,
                  content_type: contentType,
                },
                extra: {
                  url: requestUrl,
                  method,
                  response_text: responseText.substring(0, 500), // Limit text length
                },
              },
            );
          }

          return {
            detail: errorDetail,
            success: false,
            statusCode: response.status,
          };
        }
      } catch (parseError) {
        const errorDetail = `Failed to parse server response (${response.status}): ${parseError instanceof Error ? parseError.message : 'Unknown parsing error'}`;

        // Capture parsing errors in Sentry (skip 401/403 auth errors and suppressed statuses)
        if (
          response.status !== 401 &&
          response.status !== 403 &&
          !suppressSentryStatuses.includes(response.status)
        ) {
          Sentry.captureException(
            new Error(`API Parse Error ${response.status}: ${errorDetail}`),
            {
              tags: {
                api_parse_error: true,
                status_code: response.status,
              },
              extra: {
                url: requestUrl,
                method,
                parse_error: parseError,
              },
            },
          );
        }

        return {
          detail: errorDetail,
          success: false,
          statusCode: response.status,
        };
      }
    }

    // Handle 204 No Content response
    if (response.status === 204) {
      return { success: true };
    }

    if (responseType === 'text') {
      return { data: (await response.text()) as T, success: true };
    }

    if (responseType === 'blob') {
      return {
        data: (await response.blob()) as T,
        success: true,
        headers: response.headers,
      };
    }

    try {
      const data = await response.json();

      if (data.detail) {
        return { detail: data.detail };
      }

      if (data.message) {
        return { message: data.message, data: data as T };
      }

      return { data };
    } catch {
      return {
        detail: 'Response parsing error',
        success: false,
      };
    }
  } catch (error) {
    // apiRequest forwards `signal` and sets no timeout of its own, so every
    // abort reaching here is one a caller asked for. Reporting is theirs.
    const isAbort =
      error instanceof DOMException && error.name === 'AbortError';

    if (!isAbort) {
      console.error('API request error:', error);
    }

    // Determine specific error type and provide informative message
    let errorDetail = 'An unexpected error occurred';
    let errorType = 'UnknownError';

    if (error instanceof TypeError) {
      if (error.message === 'Failed to fetch') {
        // Network connectivity issue or CORS error
        errorDetail = `Unable to connect to ${target} API. Please check your internet connection or try again later.`;
        errorType = 'NetworkConnectivityError';
      } else if (error.message.includes('NetworkError')) {
        errorDetail = `Network error while connecting to ${target} API. The server may be unreachable.`;
        errorType = 'NetworkError';
      } else {
        errorDetail = `Network error: ${error.message}`;
        errorType = 'TypeError';
      }
    } else if (error instanceof DOMException && error.name === 'AbortError') {
      errorDetail = `Request to ${target} API was cancelled or timed out`;
      errorType = 'AbortError';
    }
    // For generic Error instances, keep the message generic to avoid exposing internal errors to users

    // Create a new error with enhanced message for Sentry
    const enhancedError = new Error(
      error instanceof TypeError ||
      (error instanceof DOMException && error.name === 'AbortError')
        ? errorDetail
        : `${target} API request failed`,
    );
    enhancedError.name = errorType;

    // Capture enhanced error to Sentry with rich context
    if (!isAbort) {
      Sentry.captureException(enhancedError, {
        tags: {
          api_network_error: true,
          target_api: target,
        },
        extra: {
          url: requestUrl,
          method,
          original_error_type:
            error instanceof Error ? error.constructor.name : typeof error,
          original_error_message:
            error instanceof Error ? error.message : String(error),
          original_error_stack: error instanceof Error ? error.stack : null,
          backend_response:
            error instanceof Error && 'response' in error
              ? error.response
              : null,
        },
      });
    }

    return {
      detail: errorDetail,
      success: false,
    };
  }
};
